import React, { useEffect } from "react";

import styles from "./TermsAndConditions.module.scss";
import Footer from "../../components/Footer";
import Header from "../../components/Header";

const LEGAL_INTRO = {
  company: "Penspace Enterprises Pvt. Ltd.",
  effectiveDate: "March 25, 2026",
  paragraphs: [
    'These Terms of Service ("Terms") govern your access to and use of the Penspace website, mobile applications, and related services (collectively, the "Service") operated by Penspace Enterprises Pvt. Ltd. ("Penspace", "Company", "we", "our", or "us").',
    "By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you must not use the Service.",
  ],
};

const LEGAL_SECTIONS = [
  {
    title: "1. Object and Scope of These Terms",
    blocks: [
      { type: "p", text: 'These Terms define the legal relationship between you ("User") and Penspace.' },
      { type: "p", text: "They apply to:" },
      {
        type: "ul",
        items: [
          "Website: https://penspace.in",
          "Mobile applications (iOS and Android)",
          "All current and future features of the platform",
        ],
      },
      { type: "p", text: "These Terms apply at the time of:" },
      {
        type: "ul",
        items: ["Account registration", "Subscription purchase", "Use of the platform"],
      },
      { type: "p", text: "Penspace does not accept any conflicting user terms unless agreed in writing." },
    ],
  },
  {
    title: "2. About Penspace",
    blocks: [
      { type: "p", text: "Penspace is a digital learning platform that provides:" },
      {
        type: "ul",
        items: [
          "Transformative summaries of non-fiction books",
          "Educational insights and structured learning content",
          "Reading productivity tools",
          "Personalized recommendations",
          "Highlighting and note-taking features",
          "OCR-based text capture (processed locally)",
          "Community features (public sharing, future updates)",
        ],
      },
      {
        type: "p",
        text: "Penspace content is designed to help users understand key ideas efficiently and is intended for educational purposes only.",
      },
    ],
  },
  {
    title: "3. Eligibility",
    blocks: [
      { type: "p", text: "You must:" },
      {
        type: "ul",
        items: [
          "Be at least 18 years old",
          "Have the legal capacity to enter into a binding agreement",
        ],
      },
      { type: "p", text: "By using Penspace, you confirm that you meet these requirements." },
    ],
  },
  {
    title: "4. User Account and Registration",
    blocks: [
      { type: "p", text: "To access the Service, you must create an account using:" },
      {
        type: "ul",
        items: ["Google login", "Apple login", "Email and password"],
      },
      { type: "p", text: "You agree to:" },
      {
        type: "ul",
        items: [
          "Provide accurate and complete information",
          "Keep your login credentials secure",
          "Be responsible for all activities under your account",
        ],
      },
      { type: "p", text: "Accounts are personal and may not be shared." },
      { type: "p", text: "Penspace may require identity verification if necessary." },
    ],
  },
  {
    title: "5. Free and Paid Services",
    blocks: [
      { type: "p", text: "Penspace offers:" },
      { type: "h3", text: "5.1 Free Access" },
      { type: "p", text: "Limited access to features without payment." },
      { type: "h3", text: "5.2 Paid Subscription" },
      { type: "p", text: "Full access through:" },
      {
        type: "ul",
        items: ["Monthly subscription", "Yearly subscription"],
      },
      { type: "p", text: "Features available depend on your subscription type." },
    ],
  },
  {
    title: "6. Subscription Terms",
    blocks: [
      { type: "p", text: "Subscriptions are processed through:" },
      {
        type: "ul",
        items: ["Apple App Store", "Google Play Store"],
      },
      { type: "p", text: "Key Terms:" },
      {
        type: "ul",
        items: [
          "Subscriptions auto-renew unless canceled",
          "Billing is handled by Apple/Google",
          "Prices are displayed during purchase",
          "Taxes may be included as applicable",
        ],
      },
      { type: "p", text: "You are responsible for managing your subscription via your app store account." },
    ],
  },
  {
    title: "7. Free Trial",
    blocks: [
      { type: "p", text: "Penspace may offer a 7-day free trial." },
      { type: "p", text: "If you provide payment details:" },
      {
        type: "ul",
        items: [
          "The trial will automatically convert into a paid subscription after the trial period",
          "You must cancel before the trial ends to avoid charges",
        ],
      },
      { type: "p", text: "Penspace reserves the right to modify or discontinue trials." },
    ],
  },
  {
    title: "8. Refunds and Cancellation",
    blocks: [
      { type: "p", text: "All payments are processed by:" },
      {
        type: "ul",
        items: ["Apple App Store", "Google Play Store"],
      },
      { type: "p", text: "Refunds are governed by their respective policies." },
      { type: "p", text: "Penspace does not directly issue refunds." },
      { type: "p", text: "You may cancel your subscription at any time via your app store settings." },
      { type: "p", text: "Cancellation takes effect at the end of the billing period." },
    ],
  },
  {
    title: "9. Intellectual Property Rights",
    blocks: [
      { type: "p", text: "All content on Penspace, including:" },
      {
        type: "ul",
        items: [
          "Book summaries",
          "Insights and explanations",
          "Illustrations and designs",
          "Software and UI",
          "Logos and branding",
        ],
      },
      { type: "p", text: "are owned by Penspace or its licensors." },
      { type: "h3", text: "License Granted" },
      { type: "p", text: "Penspace grants you a:" },
      {
        type: "ul",
        items: ["Non-exclusive", "Non-transferable", "Limited license"],
      },
      { type: "p", text: "to use the Service for personal, non-commercial use only." },
      { type: "h3", text: "Restrictions" },
      { type: "p", text: "You may NOT:" },
      {
        type: "ul",
        items: [
          "Copy or reproduce content",
          "Redistribute summaries",
          "Sell or monetize content",
          "Scrape or extract data",
          "Reverse engineer the platform",
        ],
      },
    ],
  },
  {
    title: "10. Transformative Content Clause",
    blocks: [
      { type: "p", text: "Penspace provides original, transformative summaries and interpretations of books." },
      { type: "p", text: "These summaries:" },
      {
        type: "ul",
        items: [
          "Do not replace the original works",
          "Do not reproduce full copyrighted material",
          "Are created for educational purposes",
        ],
      },
      { type: "p", text: "Users are encouraged to support original authors." },
    ],
  },
  {
    title: "11. OCR Feature",
    blocks: [
      { type: "p", text: "Penspace may allow users to scan text using their device camera." },
      { type: "p", text: "OCR processing occurs locally on the device" },
      { type: "p", text: "Penspace does not store raw images unless saved by the user" },
      { type: "p", text: "You agree:" },
      {
        type: "ul",
        items: [
          "To only scan content you legally own or have permission to use",
          "That Penspace is not responsible for misuse of this feature",
        ],
      },
    ],
  },
  {
    title: "12. Community Features",
    blocks: [
      { type: "p", text: "Penspace may allow users to:" },
      {
        type: "ul",
        items: ["Share content publicly", "Interact with other users"],
      },
      { type: "p", text: "You are responsible for your content." },
      { type: "p", text: "You agree not to post:" },
      {
        type: "ul",
        items: ["Illegal content", "Offensive or abusive material", "Spam or advertisements"],
      },
      { type: "p", text: "Penspace may moderate or remove content at its discretion." },
    ],
  },
  {
    title: "13. Acceptable Use",
    blocks: [
      { type: "p", text: "You agree NOT to:" },
      {
        type: "ul",
        items: [
          "Use bots or automated systems",
          "Attempt unauthorized access",
          "Interfere with system security",
          "Exploit the platform",
          "Engage in fraudulent activity",
        ],
      },
    ],
  },
  {
    title: "14. Account Suspension and Termination",
    blocks: [
      { type: "p", text: "Penspace may suspend or terminate your account if you:" },
      {
        type: "ul",
        items: [
          "Violate these Terms",
          "Attempt content scraping",
          "Abuse the platform",
          "Engage in illegal activity",
        ],
      },
      { type: "p", text: "Termination may occur without prior notice." },
    ],
  },
  {
    title: "15. Third-Party Services",
    blocks: [
      { type: "p", text: "Penspace uses third-party services including:" },
      {
        type: "ul",
        items: [
          "Apple App Store",
          "Google Play Store",
          "AWS (India region)",
          "Firebase Analytics",
          "Google Analytics",
        ],
      },
      { type: "p", text: "We are not responsible for third-party services or policies." },
    ],
  },
  {
    title: "16. Disclaimer of Warranties",
    blocks: [
      { type: "p", text: 'Penspace provides content "as is".' },
      { type: "p", text: "We do not guarantee:" },
      {
        type: "ul",
        items: [
          "Accuracy or completeness of content",
          "Continuous availability",
          "Suitability for specific decisions",
        ],
      },
    ],
  },
  {
    title: "17. Limitation of Liability",
    blocks: [
      { type: "p", text: "Penspace shall not be liable for:" },
      {
        type: "ul",
        items: [
          "Indirect or consequential damages",
          "Loss of data or profits",
          "Decisions based on content",
        ],
      },
      { type: "p", text: "To the maximum extent permitted by law." },
    ],
  },
  {
    title: "18. Data Protection",
    blocks: [
      {
        type: "p",
        text: "Your personal data is processed according to our Privacy Policy.",
      },
    ],
  },
  {
    title: "19. Changes to the Terms",
    blocks: [
      { type: "p", text: "Penspace may update these Terms." },
      { type: "p", text: "We will notify users of significant changes." },
      { type: "p", text: "Continued use means acceptance of updated Terms." },
    ],
  },
  {
    title: "20. Governing Law and Jurisdiction",
    blocks: [
      { type: "p", text: "These Terms are governed by the laws of India." },
      {
        type: "p",
        text: "Any disputes shall be subject to the jurisdiction of courts in Maharashtra, India.",
      },
    ],
  },
  {
    title: "21. Contact Information",
    blocks: [
      { type: "p", text: "Penspace Enterprises Pvt. Ltd." },
      {
        type: "p",
        children: (
          <>
            Email:{" "}
            <a href="mailto:info@penspace.com" className={styles.legalLink}>
              info@penspace.com
            </a>
          </>
        ),
      },
      {
        type: "p",
        children: (
          <>
            Phone:{" "}
            <a href="tel:+919003673683" className={styles.legalLink}>
              +91 9003673683
            </a>
          </>
        ),
      },
      { type: "p", text: "Registered Address:" },
      { type: "p", text: "Deglur, Nanded, Maharashtra, India – 431717" },
    ],
  },
];

function renderBlock(block, key) {
  if (block.type === "h3") {
    return (
      <h3 key={key} className={styles.subSectionTitle}>
        {block.text}
      </h3>
    );
  }
  if (block.type === "ul") {
    return (
      <ul key={key}>
        {block.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "p") {
    if (block.children) {
      return <p key={key}>{block.children}</p>;
    }
    return <p key={key}>{block.text}</p>;
  }
  return null;
}

export default function TermsAndConditions() {
  useEffect(() => {
    document.title = "Terms & Conditions - Penspace";
    return () => {
      document.title = "Penspace";
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      <Header />

      <section className={styles.legalPage}>
        <div className={styles.container}>
          <div className={styles.legalContent}>
            <p className={styles.companyLine}>{LEGAL_INTRO.company}</p>
            <h1 className={styles.legalTitle}>Terms of Service</h1>
            <p className={styles.legalSubtitle}>Effective Date: {LEGAL_INTRO.effectiveDate}</p>

            {LEGAL_INTRO.paragraphs.map((text, i) => (
              <p key={i} className={styles.introParagraph}>
                {text}
              </p>
            ))}

            {LEGAL_SECTIONS.map((section, index) => (
              <div key={index} className={styles.legalSection}>
                <h2>{section.title}</h2>
                {section.blocks.map((block, i) => renderBlock(block, i))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
